import { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { api } from "../lib/api";

const usd = (v, dp = 6) =>
  v == null ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp })}`;
const inr = (v) =>
  v == null ? "—" : `\u20B9${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mins = (v) => (v == null ? "—" : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}m`);
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

function Block({ title, d, tid, tone }) {
  if (!d) return null;
  return (
    <div className={`border p-5 ${tone} space-y-3`} data-testid={tid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{title}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          ["AI cost / minute", usd(d.ai_cost_per_minute_usd, 5)],
          ["Revenue / minute", `${usd(d.revenue_per_minute_usd, 5)} · ${inr(d.revenue_per_minute_inr)}`],
          ["Contribution", usd(d.contribution_usd, 4)],
          ["Margin", pct(d.margin_pct)],
          ["Cost / revenue", pct(d.cost_to_revenue_pct)],
          ["Videos · minutes", `${d.videos_with_full_data} · ${mins(d.minutes)}`],
        ].map(([label, value]) => (
          <div key={label}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="text-lg font-mono text-white mt-0.5 break-all">{value}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-zinc-600">
        AI cost {usd(d.ai_cost_usd, 4)} · attributed revenue {inr(d.plan_revenue_inr)}
        {d.videos_excluded_for_missing_data
          ? ` · ${d.videos_excluded_for_missing_data} video(s) excluded for missing duration or cost telemetry`
          : ""}
      </div>
    </div>
  );
}

/** Read-only unit economics. Revenue is attributed from plan list price, never invented. */
export default function UnitEconomics({ rangeQuery }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/admin/spend/unit-economics?${rangeQuery}`);
        if (alive) setData(d);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [rangeQuery]);

  if (!data) return null;

  return (
    <section data-testid="unit-economics-section">
      <h2 className="font-heading text-2xl font-bold tracking-tight mb-3 flex items-center gap-2">
        <Scale className="w-4 h-4 text-emerald-400" /> Unit economics
      </h2>

      {!data.available ? (
        <div
          className="border border-amber-500/30 bg-amber-500/[0.04] p-4 text-sm text-amber-200"
          data-testid="unit-economics-unavailable"
        >
          {data.message}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Block
              title="Customer videos (paid + free tier)"
              d={data.customer}
              tid="unit-economics-customer"
              tone="border-zinc-800 bg-zinc-900/40"
            />
            <Block
              title="Admin / internal videos — excluded from customer metrics"
              d={data.internal}
              tid="unit-economics-internal"
              tone="border-blue-500/25 bg-blue-500/[0.03]"
            />
          </div>

          <div
            className="border border-zinc-800 bg-zinc-900/40 p-4 text-[11px] font-mono text-zinc-400"
            data-testid="unit-economics-cash"
          >
            Cash actually collected in this range:{" "}
            <span className="text-emerald-300">{inr(data.cash_collected?.amount_inr)}</span> across{" "}
            {data.cash_collected?.transactions || 0} paid transaction(s). {data.cash_collected?.note}
          </div>

          <div className="border border-zinc-800 overflow-x-auto" data-testid="unit-economics-table">
            <table className="w-full text-xs">
              <thead className="bg-zinc-900/60 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left px-3 py-3">Job</th>
                  <th className="text-left px-3 py-3">Plan</th>
                  <th className="text-left px-3 py-3">Revenue basis</th>
                  <th className="text-right px-3 py-3">Duration</th>
                  <th className="text-right px-3 py-3">AI cost</th>
                  <th className="text-right px-3 py-3">$/min</th>
                  <th className="text-right px-3 py-3">Revenue ₹</th>
                  <th className="text-right px-3 py-3">₹/min</th>
                  <th className="text-right px-3 py-3">Contribution</th>
                  <th className="text-right px-3 py-3">Margin</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((r) => (
                  <tr
                    key={r.job_id}
                    className="border-t border-zinc-800/60 hover:bg-zinc-900/40"
                    data-testid={`unit-economics-row-${r.job_id}`}
                  >
                    <td className="px-3 py-2.5 font-mono text-zinc-300">{r.job_id.slice(0, 16)}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${
                          r.internal
                            ? "border-blue-500/40 text-blue-300"
                            : r.plan_key === "free"
                            ? "border-zinc-700 text-zinc-500"
                            : "border-emerald-500/40 text-emerald-300"
                        }`}
                      >
                        {r.plan_key}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-zinc-500 font-mono text-[10px]">{r.revenue_basis}</td>
                    <td className="px-3 py-2.5 text-right text-zinc-400">{mins(r.duration_minutes)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-white">{usd(r.ai_cost_usd, 4)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-400">
                      {usd(r.ai_cost_per_minute_usd, 4)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-300">
                      {inr(r.plan_revenue_inr)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-400">
                      {inr(r.plan_revenue_per_minute_inr)}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right font-mono ${
                        r.contribution_usd == null
                          ? "text-zinc-500"
                          : r.contribution_usd >= 0
                          ? "text-emerald-300"
                          : "text-red-300"
                      }`}
                    >
                      {usd(r.contribution_usd, 4)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-zinc-400">{pct(r.margin_pct)}</td>
                  </tr>
                ))}
                {(data.rows || []).length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center text-zinc-500 py-6" data-testid="unit-economics-empty">
                      No video with cost telemetry in this range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <ul className="text-[10px] font-mono text-zinc-600 space-y-1">
            {(data.notes || []).map((n) => (
              <li key={n.slice(0, 24)}>· {n}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
